import * as React from 'react';
import * as S from './styles';
import * as T from './types';
import TopTextInput from '../TopTextInput';
import EditIcon from 'common/icons/editIcon';
import QuotationIcon from 'common/icons/quotationIcon';
import AvatarIcon from 'common/icons/avatarIcon';
import BigArrowIcon from 'common/icons/bigArrowIcon';
import { capitalizeFirstLetter, getFixedOpacity, logFunction } from '../../state/utils';
import { getRatingRecommended } from '../utils';
import { createRGBA } from '../../state/utils';
import { Context } from '../../context';
import EmptyMessage from '../EmptyMessage';

const TopReviewsWidget: React.FC<T.ITopReviewsProps> = (props) => {
    const reviews = props.widgetsConfig?.topReviews?.reviews ?? [];
    const [intervalId, setIntervalId] = React.useState<any>();
    const [intervalIndex, setIntervalIndex] = React.useState<number | undefined>(undefined);
    const [animatedReviews, setAnimatedReviews] = React.useState<T.IAnimatedReviews[]>([]);
    const [textOpened, setTextOpened] = React.useState<boolean>(false);
    const [viewSeeMoreLess, setViewSeeMoreLess] = React.useState<boolean>(false);
    const context = React.useContext(Context);

    const createColor = (color?: string, opacity?: number, index?: number) => {
        return createRGBA(
            color ?? "#4da0ff",
            getFixedOpacity(opacity) / (index === undefined || intervalIndex === index ? 1 : 3)
        );
    }

    const getColor = (attribute: string, index?: number, opacity?: number) => {
        return context.editingStatus[attribute]?.id === "topReviews" ? 
            createColor(context.editingStatus[attribute].color, opacity ?? context.editingStatus[attribute].opacity, index) :
            createColor(props.widgetsConfig.topReviews[`${attribute}Color`], opacity ?? props.widgetsConfig.topReviews[`${attribute}Opacity`], index);
    }

    const handleOpenSearchReviews = (e: React.MouseEvent<HTMLDivElement>) => {
        props.handleShowSearchReviews({
            show: true,
            posX: e.clientX - 750,
            posY: e.clientY + 50,
        })
    }

    const handleNavigateReview = (step: number) => {
        setTextOpened(false);
        if (intervalIndex !== undefined && reviews.length) {
            let currentReviewIndex = intervalIndex;
            currentReviewIndex += step;
            if (currentReviewIndex === reviews.length) currentReviewIndex = 0;
            if (currentReviewIndex === -1) currentReviewIndex = reviews.length - 1;
            if (currentReviewIndex >= 0 && currentReviewIndex < reviews.length) setIntervalIndex(currentReviewIndex);
        }
    }

    const handleSelectReview = (index: number) => {
        setTextOpened(false);
        setIntervalIndex(index);
    }

    const handelSeeMoreLess = () => {
        setTextOpened(!textOpened);
    }

    const automaticScroll = () => {
        if (props.widgetsConfig.topReviews.automaticScrollTime) {
            if (intervalId) clearInterval(intervalId);
            const interval = setInterval(() => {
                setIntervalIndex(intIndex => {
                    if (intIndex === undefined || intIndex + 1 === reviews.length) return 0;
                    return intIndex + 1;
                });
            }, props.widgetsConfig.topReviews.automaticScrollTime * 1000);
            setIntervalId(interval);
        }
    }

    React.useEffect(() => {
        if (intervalIndex !== undefined && reviews.length) {
            if (reviews[intervalIndex].detail && reviews[intervalIndex].detail.length > 300) {
                setViewSeeMoreLess(true);
            }
            else {
                setViewSeeMoreLess(false);
            }
            if (animatedReviews.length === 0) {
                setAnimatedReviews([
                    {key: Math.random(), review: reviews[0], animationClass: "animatedFadeIn"},
                ]);
            } else {
                const oldAnimatedReview = animatedReviews.find(ar => ar.animationClass === "animatedFadeIn");
                const oldReviewIndex = reviews.findIndex(r => r.id === oldAnimatedReview?.review.id);
                if (oldAnimatedReview) {
                    let animationClass: T.ReviewAnimationType = oldReviewIndex < intervalIndex ? "animatedFadeOutRight" : "animatedFadeOutLeft";
                    if (oldReviewIndex === 0 && intervalIndex === reviews.length - 1) animationClass = "animatedFadeOutLeft";
                    if (oldReviewIndex === reviews.length - 1 && intervalIndex === 0) animationClass = "animatedFadeOutRight";
                    setAnimatedReviews([
                        {key: Math.random(), review: reviews[oldReviewIndex], animationClass},
                        {key: Math.random(), review: reviews[intervalIndex], animationClass: "animatedFadeIn"}
                    ]);
                }
            }
        }
    }, [intervalIndex]);

    React.useEffect(() => {
        if (props.widgetsConfig?.topReviews) {
            if (props.widgetsConfig.topReviews.automaticScroll) automaticScroll();
            else {
                clearInterval(intervalId);
                setIntervalId(undefined);
            }
        }
    }, [
        props.widgetsConfig.topReviews.automaticScroll,
        props.widgetsConfig.topReviews.automaticScrollTime,
        props.widgetsConfig.topReviews.reviews,
    ]);

    React.useEffect(() => {
        if (reviews) {
            setIntervalIndex(reviews[0] ? 0 : undefined);
        }
    }, [JSON.stringify(reviews)]);

    const renderShadowBoxes = () => {
        if (intervalIndex !== undefined && reviews.length) {
            const shadowReviews = reviews.map((r, index) => {
                let shadowBox: T.IShadowBox = {
                    top: 0,
                    height: 100,
                    width: 0,
                }
                const diff = logFunction(Math.abs(index - intervalIndex) + 1, 1.5, undefined, -0.2);
                if (index > intervalIndex) {
                    shadowBox.left = 30 * - diff;
                } else if (index < intervalIndex) {
                    shadowBox.right = 30 * - diff;
                }
                shadowBox.top = 4 * diff;
                shadowBox.width = 30 * diff;
                shadowBox.height -= 8 * diff;
                return shadowBox;
            });
            return shadowReviews.map((shadowReview, index) =>
                <S.ShadowBox
                    key={index}
                    leftValue={shadowReview.left !== undefined ? `${shadowReview.left}px` : undefined}
                    rightValue={shadowReview.right !== undefined ? `${shadowReview.right}px` : undefined}
                    topValue={`${shadowReview.top}%`}
                    widthValue={`${shadowReview.width}px`}
                    heightValue={`${shadowReview.height}%`}
                    style={{backgroundColor: getColor("shadow")}}
                />
            );
        }
    }

    const titleColor = context.editingStatus.titleText?.id === "topReviews" ? context.editingStatus.titleText.color : props.widgetsConfig.topReviews.titleTextColor ?? "#000000";
    const bodyColor = context.editingStatus.bodyText?.id === "topReviews" ? context.editingStatus.bodyText.color : props.widgetsConfig.topReviews.bodyTextColor ?? "#000000";

    const renderWidgetBoxes = () => {
        const starColor= context.editingStatus.star?.id === "topReviews" ? createRGBA(context.editingStatus.star.color ?? "#FFD84D", context.editingStatus.star.opacity) : createRGBA(props.widgetsConfig.topReviews.starColor ?? "#FFD84D", props.widgetsConfig.topReviews.starOpacity);
        return animatedReviews.map((animatedReview, index) => {
            return (
                <S.WidgetBox 
                    key={animatedReview.key}
                    className={animatedReview.animationClass}
                >
                    <S.QuotationIconContainer>
                        <QuotationIcon />
                    </S.QuotationIconContainer>
                    <S.Detail
                        style={{color: bodyColor}}
                        textOpenedValue={textOpened}
                        viewSeeMoreLessValue={viewSeeMoreLess}
                    >
                        {animatedReview.review?.detail}
                    </S.Detail>
                    {
                        viewSeeMoreLess &&
                        <S.SeeMoreLess onClick={handelSeeMoreLess}>
                        {
                            textOpened?'See Less':'See More'
                        }
                        </S.SeeMoreLess>
                    }
                    <S.WidgetBoxBottomContainer>
                        <S.WidgetBoxBottom>
                            <S.UserInfo>
                                <S.AvatarIconContainer>
                                    <AvatarIcon />
                                </S.AvatarIconContainer>
                                <S.UserLabel
                                    style={{color: bodyColor}}
                                >
                                    {animatedReview.review?.reviewer}
                                </S.UserLabel>
                            </S.UserInfo>
                            <S.StarsContainer>
                                {animatedReview.review && 
                                getRatingRecommended({
                                    provider: animatedReview.review.provider,
                                    recommended: animatedReview.review.recommended,
                                    rating: animatedReview.review.rating,
                                    starStyle: {width: "20px", height: "20px"},
                                    showEmptyStars: false,
                                    thumbtext:false,
                                    starColor
                                })}
                            </S.StarsContainer>
                        </S.WidgetBoxBottom>
                        <S.ReviewLink
                            onClick={() => window.open(animatedReview.review?.url, "_blank")}
                        >
                            {`Via ${capitalizeFirstLetter(animatedReview.review?.provider??'')}`}
                        </S.ReviewLink>
                    </S.WidgetBoxBottomContainer>
                </S.WidgetBox>
            );
        });
    }

    return (
        <S.Root>
            <S.Title>
                <TopTextInput
                    {...props}
                    textTag={props.widgetsConfig.topReviews.titleTag}
                    widgetType={"topReviews"}
                    style={{height: "68px"}}
                    inputStyle={{
                        width: "608px",
                        height: "68px",
                        border: "solid 2px #e7e7ec",
                        borderRadius: "5px",
                        color: titleColor,
                    }}
                    textStyle={{
                        color: titleColor,
                    }}
                />
            </S.Title>
            <S.WidgetBoxContainer>
                <S.WidgetBoxCenter style={{borderColor: getColor("shadow", undefined, 100)}} >
                    {renderShadowBoxes()}
                    {
                        intervalIndex !== undefined && animatedReviews.length > 0 &&
                        <>
                            <S.ArrowContainer
                                leftValue='-90px'
                                transformValue='rotate(180deg)'
                                onClick={() => handleNavigateReview(-1)}
                            >
                                <BigArrowIcon />
                            </S.ArrowContainer>
                            {renderWidgetBoxes()}
                            <S.ArrowContainer
                                rightValue='-90px'
                                onClick={() => handleNavigateReview(1)}
                            >
                                <BigArrowIcon />
                            </S.ArrowContainer>
                        </>
                    }
                    {
                        animatedReviews.length === 0 &&
                        <S.WidgetBox>
                            <S.QuotationIconContainer>
                                <QuotationIcon />
                            </S.QuotationIconContainer>
                            <EmptyMessage
                                message="To feature a review, click on the pencil icon to the right, filter and select the review you'd like to showcase, and save."
                                containerStyle={{height: "280px"}}
                                style={{width: "357px"}}
                            />
                        </S.WidgetBox>
                    }
                    <S.EditIconContainer
                        onClick={handleOpenSearchReviews}
                    >
                        <EditIcon />
                    </S.EditIconContainer>
                </S.WidgetBoxCenter>
            </S.WidgetBoxContainer>
            <S.DotsContainer>
                {
                    reviews.map((review, index) => {
                        return(
                            <S.Dot
                                key={index}
                                onClick={() => handleSelectReview(index)}
                                backgroundColorValue={getColor("dot", index)}
                            />
                        );
                    })
                }
            </S.DotsContainer>
        </S.Root>
    )
}

export default TopReviewsWidget;
